import { secureStorage } from './secure-storage';
import { getDeviceKey } from './keystore';
import { storageKey } from './storage-keys';

const KEYS = {
  PAIRING_CODE: storageKey('pairing_code'),
};

// Confusion-resistant alphabet (excludes 0/O, 1/I/L). Pairing code stays
// readable even when stickered to a wall in low light.
const ALPHABET = 'ABCDEFGHJKMNPQRSTVWXYZ23456789';

function generatePairingCode(length = 8): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array)
    .map((b) => ALPHABET[b % ALPHABET.length])
    .join('');
}

/**
 * Format code with dash for display: "A7XK2M9P" → "A7XK-2M9P".
 */
export function formatCode(code: string): string {
  if (code.length !== 8) return code;
  return `${code.slice(0, 4)}-${code.slice(4)}`;
}

/**
 * Get-or-create the pairing code for this boot. The deviceKey (signing
 * keypair in hardware enclave) is the credential — not the code. The
 * code is just a one-time human-readable identifier so the operator can
 * connect this physical screen to a Board record in the dashboard.
 */
export async function getOrCreatePairingCode(): Promise<string> {
  const existing = await secureStorage.get(KEYS.PAIRING_CODE);
  if (existing) return existing;
  const code = generatePairingCode(8);
  await secureStorage.set(KEYS.PAIRING_CODE, code);
  return code;
}

/**
 * Expose the device's public-key material to caller code. The private
 * half lives in the keystore and is never returned.
 */
export async function getDeviceCredentials(): Promise<{
  publicKeyDer: string;
  fingerprint: string;
  keyAlg: 'ECDSA_P256_SHA256';
}> {
  const key = await getDeviceKey();
  return {
    publicKeyDer: key.publicKeyDer,
    fingerprint: key.fingerprint,
    keyAlg: key.alg,
  };
}

/**
 * Sign a buffer with the device's private key. Used during pairing
 * confirm to prove possession of the keypair the player registered.
 * Every other authenticated request is signed inside api-client.
 */
export async function signWithDeviceKey(payload: Uint8Array): Promise<string> {
  const key = await getDeviceKey();
  return key.sign(payload);
}

/**
 * Clear the pairing code (used after successful activation or reset).
 * The keystore key persists — that's the whole point. A reset that wipes
 * the key is a separate "factory reset" path.
 */
export async function clearPairingCredentials(): Promise<void> {
  await secureStorage.remove(KEYS.PAIRING_CODE);
}
